import Icon from "@components/ui/icon";
import { MediaLibCategoryEnum } from "@editor/modules/media-lib/types";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { useState } from "react";
import type { IMediaFile } from "@/shared/types";
import { useMediaLibDialogStoreShallow } from "../../../stores";
import { useMediaLibPanelStoreShallow } from "../stores";
import { MediaLibDialogBulkFilesArchiveDialog } from "./media-lib-dailog-bulk-files-archive-dialog";
import { MediaLibDialogBulkFilesDeleteDialog } from "./media-lib-dailog-bulk-files-delete-dialog";
import { MediaLibDialogBulkFilesMoveDialog } from "./media-lib-dailog-bulk-files-move-dialog";

interface IProps {
  bulkUpdateFiles: (ids: string[], data: Partial<IMediaFile>) => void;
  bulkDeleteFiles: (ids: string[]) => void;
}

export const MediaLibDialogFilesBulkActions = ({
  bulkUpdateFiles,
  bulkDeleteFiles,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { items, isSubmitting, setSelectedItems } =
    useMediaLibDialogStoreShallow((s) => ({
      items: s.selectedItems,
      isSubmitting: s.isSubmitting,
      setSelectedItems: s.setSelectedItems,
    }));

  const { category } = useMediaLibPanelStoreShallow((s) => ({
    category: s.category,
  }));

  const isArchive = category === MediaLibCategoryEnum.Archive;

  const handleMove = async () => {
    dispatch(
      openDialog({
        id: MediaLibDialogBulkFilesMoveDialog.dialogId,
        content: MediaLibDialogBulkFilesMoveDialog,
        props: {
          bulkUpdateFiles,
        },
      }),
    );
  };

  const [prevIsArchive, setPrevIsArchive] = useState(isArchive);
  if (isArchive !== prevIsArchive) {
    setPrevIsArchive(isArchive);
    setSelectedItems([]);
  }

  const handleRemove = async () => {
    dispatch(
      openDialog({
        id: MediaLibDialogBulkFilesArchiveDialog.dialogId,
        content: MediaLibDialogBulkFilesArchiveDialog,
        props: {
          bulkUpdateFiles,
        },
      }),
    );
  };

  const handleDelete = async () => {
    dispatch(
      openDialog({
        id: MediaLibDialogBulkFilesDeleteDialog.dialogId,
        content: MediaLibDialogBulkFilesDeleteDialog,
        props: {
          bulkDeleteFiles,
        },
      }),
    );
  };

  return (
    <div
      className={cn(
        "tw-absolute tw-bottom-2 tw-left-1/2 tw-z-50 tw-flex tw-w-fit -tw-translate-x-1/2 tw-items-center tw-rounded-lg tw-border tw-border-blue-50 tw-bg-white tw-p-2 tw-px-1 tw-py-[5px] tw-shadow-main tw-transition-opacity",
        items.length > 0 ? "tw-opacity-100" : "tw-opacity-0",
      )}
    >
      <button
        className={cn(
          "tw-ml-2 tw-outline-none",
          isSubmitting && "tw-cursor-not-allowed",
        )}
        type="button"
        onClick={() => setSelectedItems([])}
        disabled={isSubmitting}
      >
        <Icon icon="ri-close-line" className="tw-mr-1.5 tw-text-blue-800" />
      </button>
      <span className="tw-text-[11px] tw-font-semibold tw-text-blue-800">
        Selected media: {items.length}
      </span>
      <div className="tw-ml-6 tw-flex tw-items-center tw-space-x-1">
        <button
          type="button"
          className="tw-flex tw-items-center tw-rounded tw-px-2 tw-py-[5px] tw-text-[11px] tw-font-semibold tw-text-blue-800 tw-outline-none tw-transition-colors hover:tw-bg-blue-50"
          onClick={handleMove}
        >
          <Icon
            icon="ri-folder-transfer-line"
            className="tw-mr-1 tw-text-[16px]/none"
          />
          Move
        </button>

        <button
          type="button"
          className="tw-flex tw-items-center tw-rounded tw-px-2 tw-py-[5px] tw-text-[11px] tw-font-semibold tw-text-[#D43548] tw-outline-none tw-transition-colors hover:tw-bg-[#FFEEF0]"
          onClick={isArchive ? handleDelete : handleRemove}
        >
          <Icon
            icon="ri-delete-bin-6-line"
            className="tw-mr-1 tw-text-[16px]/none"
          />
          {isArchive ? "Delete" : "Archive"}
        </button>
      </div>
    </div>
  );
};
